import constants from "../../constants";
import { useContext, useState } from "react";
import useAxiosPrivate from "../useAxiosPrivate";
import AlertContext from "../../context/AlertContext";
import useCommonFunctionHooks from "../useCommonFunctionHooks";
import useS3FileManagement from "../useS3FileManagement";
import { getDataFromStorage } from "../../util";

export default function CameraPlacementHooks() {
  const Alert = useContext(AlertContext);

  const axios = useAxiosPrivate();

  const { getHeaderDetailsForAxios, getCatchBlockDetails } =
    useCommonFunctionHooks();
  const { getPreSignedUrl } = useS3FileManagement()

  const [cameraPlacementDetail, setCameraPlacementDetail] = useState({
    button: true,
  });

  const verifyPic = async (data) => {
    try {
      // Validate that data (file path) is provided
      if (!data) {
        console.error("❌ No file path provided for verification");
        Alert.setIsLoaded(false);
        setCameraPlacementDetail((prev) => ({
          ...prev,
          button: true,
          status: constants.RESULT_STATUS.TECHNICAL_ERROR,
          msg: "No image path received. Please capture the image again.",
        }));
        return;
      }

      console.log("🔍 Verifying image:", data);

      const pic = await getPreSignedUrl(data, "DP", data?.split(".")?.pop(), "GET");

      // Validate that we got a valid pre-signed URL
      if (!pic || !pic.url) {
        console.error("Failed to get pre-signed URL for image");
        Alert.setIsLoaded(false);
        setCameraPlacementDetail((prev) => ({
          ...prev,
          button: true,
          status: constants.RESULT_STATUS.TECHNICAL_ERROR,
          msg: "Image not found. Please ensure your mobile device has captured the image. Check that you see the 'Image Captured' notification on your mobile.",
        }));
        return;
      }

      Alert.setTestData((prev) => ({
        ...prev,
        verifyPic: true,
      }));

      Alert.setVerifyImg(pic.url);
      Alert.setIsLoaded(true);

      setCameraPlacementDetail((prev) => ({
        ...prev,
        button: true,
        status: constants.RESULT_STATUS.FAILURE_ATTEMPT,
        msg: `Please wait! We are checking if your phone camera is aligned correctly. It may take a minute; please do not refresh the page or 
        change your mobile orientation.`,
      }));

      const requestPayload = {
        key: data,
        from_s3: "true",
        setup_check: "true",
        learnerid: getDataFromStorage("learnerid"),
        bucket: process.env.REACT_APP_DP_S3_BUCKET_NAME,
      };

      console.log("📤 Sending verification request:", requestPayload);

      axios
        .post(
          "node/learner/dp/verify/pic",
          JSON.stringify(requestPayload),
          getHeaderDetailsForAxios()
        )
        .then((res) => {
          console.log("📥 Verification response:", res.data);

          if (res.data.resultCode === constants.RESULT_STATUS.SUCCESS) {
            Alert.setIsLoaded(false);
            setCameraPlacementDetail((prev) => ({
              ...prev,
              button: false,
              status: constants.RESULT_STATUS.SUCCESS,
              msg: `We've verified your side view, screen, and background. Click "Proceed" to begin with the test.`,
            }));
          } else {
            Alert.setIsLoaded(false);
            const errorMessage = res.data.msg || res.data.data?.failure || "Position verification failed";
            console.error("❌ Verification failed:", errorMessage);
            setCameraPlacementDetail((prev) => ({
              ...prev,
              button: true,
              status: constants.RESULT_STATUS.TECHNICAL_ERROR,
              msg: `Your current position isn't correct. Adjust your side view, screen, and background, then click the 'Check Camera Alignment' 
              button to try again.`,
            }));
          }
        })
        .catch((error) => {
          console.error("❌ Verification request failed:", error);
          Alert.setIsLoaded(false);
          setCameraPlacementDetail((prev) => ({
            ...prev,
            button: true,
            status: constants.RESULT_STATUS.TECHNICAL_ERROR,
            msg: "Failed to verify image. Please check your connection and try again.",
          }));
          getCatchBlockDetails(error);
        });
    } catch (error) {
      console.error("❌ Error in verifyPic:", error);
      Alert.setIsLoaded(false);
      setCameraPlacementDetail((prev) => ({
        ...prev,
        button: true,
        status: constants.RESULT_STATUS.TECHNICAL_ERROR,
        msg: "An error occurred during verification. Please try again.",
      }));
    }
  };

  return { verifyPic, cameraPlacementDetail, setCameraPlacementDetail };
}
